// 로컬라이즈 라우팅 훅
// ✅ P0-4 + P1-4 FIX: Simplified with centralized language management
import { useEffect, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { getLogger } from '@/lib/logger/logger';

import { useLanguage } from './useLanguage';
import { useRegion } from './useRegion';

const logger = getLogger('useLocalizedRouting');

interface UseLocalizedRoutingOptions {
  preserveQueryParams?: boolean;
}

export const useLocalizedRouting = (options: UseLocalizedRoutingOptions = {}) => {
  const { preserveQueryParams = true } = options;
  const { regionInfo, isOverseas } = useRegion();
  const location = useLocation();
  const { i18n } = useTranslation();
  const { changeLanguage } = useLanguage();
  const initializedRef = useRef(false);

  // 언어 자동 전환 로직
  // ✅ FIX: Prevent infinite loop with useLanguage by only auto-switching on initial load
  // ✅ FIX: Overseas users must always use English (override manual selection)
  useEffect(() => {
    if (!regionInfo) {
      logger.debug('[useLocalizedRouting] No region info yet, waiting...');
      return;
    }

    logger.debug('[useLocalizedRouting] Effect triggered:', {
      isOverseas,
      currentLanguage: i18n.language,
      pathname: location.pathname,
    });

    let targetLanguage: 'ko' | 'en' | null = null;

    // 해외 사용자: EN / JA 모두 허용 (URL이 언어의 source of truth)
    // 국내 언어(ko)인 경우에만 EN으로 전환
    if (isOverseas) {
      if (i18n.language === 'ko') {
        logger.info(`[useLocalizedRouting] Overseas user with Korean — forcing English`, {
          from: i18n.language,
        });
        void changeLanguage('en');
      }
      return;
    }

    // 국내 사용자: 초기 로드 시에만 자동 전환
    const hasManualSelection = !!localStorage.getItem('i18nextLng');
    if (initializedRef.current || hasManualSelection) {
      logger.debug('[useLocalizedRouting] Skipping auto-switch for domestic user:', {
        initialized: initializedRef.current,
        hasManualSelection,
      });
      return;
    }

    // 국내 사용자는 URL에 /en/이 있는지에 따라 언어 결정
    const hasEnInPath = location.pathname.includes('/en/');
    targetLanguage = hasEnInPath ? 'en' : 'ko';

    // 언어 변경이 필요한 경우에만 실행
    if (targetLanguage && i18n.language !== targetLanguage) {
      logger.info(`[useLocalizedRouting] Auto-switching domestic user to ${targetLanguage}`, {
        hasEnInPath,
      });
      void changeLanguage(targetLanguage);
    }

    initializedRef.current = true;
  }, [regionInfo, isOverseas, i18n.language, changeLanguage, location.pathname]);

  // 언어 기반 경로 생성 헬퍼
  const getLocalizedPath = (path: string, language?: string) => {
    const targetLang = language ?? i18n.language;
    const cleanPath = path.replace(/^\/en\//, '/'); // 기존 /en/ 제거

    // 영어이고 국내 사용자인 경우만 /en/ 추가
    if (targetLang === 'en' && !isOverseas) {
      return `/en${cleanPath}`;
    }

    return cleanPath;
  };

  // 언어 전환 함수
  // ✅ P0-4 FIX: Use centralized changeLanguage
  const switchLanguage = async (language: 'ko' | 'en') => {
    await changeLanguage(language);

    // Note: URL update is already handled in useLanguage hook for domestic users
    // For query params preservation, we can add it here if needed
    if (preserveQueryParams && location.search) {
      // Query params are already preserved by navigate in useLanguage hook
    }
  };

  // 현재 언어가 리전과 일치하는지 확인
  const isLanguageMatchingRegion = () => {
    if (isOverseas) {
      return i18n.language === 'en' || i18n.language === 'ja';
    }
    return true; // 국내 사용자는 한국어/영어 모두 허용
  };

  return {
    currentLanguage: i18n.language,
    isOverseas,
    regionInfo,
    getLocalizedPath,
    switchLanguage,
    isLanguageMatchingRegion,
  };
};
